export const en = {
  brand: { title: 'CCBC Admin', subtitle: 'Control Panel' },
  navigation: {
    dashboard: 'Dashboard',
    admins: 'Admin Management',
    'tithe-receipts': 'Tithe Receipt Management',
    members: 'Member Management',
    'minor-members': 'Children & Teenage Ministry',
    'serve-positions': 'Serve Positions',
    organization: 'Organization Structure',
    events: 'Events',
    attendance: 'Attendance Records',
    'attendance-member': 'All Attendance Records',
    check_in: 'Check In (QR Scan)',
    continuous_checkin: 'Continuous Check-in',
    joomla: 'Joomla Articles',
    posts: 'Posts',
    'access-control': 'Access Control',
    'room-booking': 'Room Booking',
    forms: 'Forms',
    pos: 'Mini POS',
    settings: 'System Settings'
  },
  navigationDesc: {
    dashboard: 'Overview of your CCBC platform statistics and activities',
    admins: 'Manage CCBC platform administrators and their permissions',
    posts: 'Create, edit, and manage platform posts and articles',
    events: 'Manage events and their sections/classes with datetime scheduling',
    attendance: 'View and manage attendance records from check-ins',
    'attendance-member': 'List all attendance records',
    check_in: 'Check in attendance by scanning QR codes for event sections',
    continuous_checkin: 'Continuous QR scanner - scan multiple QR codes one by one without stopping',
    joomla: 'View and monitor articles from your Joomla CMS',
    'access-control': 'Configure user permissions and access levels',
    members: 'Manage member profiles, details, and roles',
    'serve-positions': 'Manage church serve positions and assignments',
    organization: 'Visualize and edit your organization structure',
    'tithe-receipts': 'Manage tithe receipt issuance and history',
    'room-booking': 'Manage room bookings and availability',
    forms: 'Create, manage, and review custom forms',
    pos: 'Sell gifts, books, and courses',
    settings: 'System settings and dangerous maintenance operations'
  },
  settings: {
    title: 'System Settings',
    description: 'Platform maintenance and system-level operations. The operations below are irreversible — use with caution.',
    dangerZone: {
      title: 'Danger Zone',
      description: 'Tick the categories to delete entirely. Only ticked data is removed. Other categories and admin accounts stay. This cannot be undone.',
      selectAll: 'Select all',
      button: 'Delete ticked categories',
      noneSelected: 'Tick at least one category',
      categories: {
        members: 'Members (full / associate / temporary)',
        minor_members: 'Children / youth',
        organization: 'Organization (districts, zones, groups, assignments)',
        serve_positions: 'Serve positions and assignments',
        attendance: 'Attendance / check-in',
        events: 'Events, sections and enrollments',
        tithe: 'Tithe receipts',
        forms: 'Forms and submissions',
        pos: 'POS products and orders',
        rooms: 'Rooms and bookings',
        posts: 'Posts'
      }
    },
    clear: {
      confirmTitle: 'Delete the ticked categories?',
      confirmBody: 'This will permanently delete: {{categories}}. Unticked categories and admin accounts are kept.',
      continue: 'Continue',
      finalTitle: 'Final confirmation',
      finalBody: 'This is the last step. Ticked categories will be permanently deleted.',
      typePrompt: 'Please type',
      finalButton: 'Permanently delete ticked categories',
      clearing: 'Deleting...',
      successTitle: 'Delete completed',
      successMsg: 'Ticked categories deleted — {{count}} rows.',
      errorTitle: 'Clear failed',
      errorMsg: 'An error occurred while clearing data. The database was rolled back.',
      resultTitle: 'Clear result',
      resultMeta: 'Deleted by {{username}} — {{count}} rows in total.',
      resultTable: 'Table',
      resultRows: 'Rows deleted',
      resultPreserved: 'Preserved tables (not cleared):'
    }
  },
  status: { online: 'Online' },
  actions: { logoutConfirm: 'Are you sure you want to logout?', logout: 'Logout' },
  language: { label: 'Language', en: 'English', 'zh-TW': '繁體中文' },
  loading: { loading: 'Loading...' },
  login: {
    title: 'CCBC Admin Panel',
    subtitle: 'Sign in to your admin account',
    username: 'Username',
    password: 'Password',
    usernamePlaceholder: 'Enter your username',
    passwordPlaceholder: 'Enter your password',
    submit: 'Sign in',
    submitting: 'Signing in...',
    loggingIn: 'Logging in...',
    footer: '© 2025 CCBC Project. All rights reserved.'
  },
  common: {
    na: 'N/A',
    back: 'Back',
    edit: 'Edit',
    delete: 'Delete',
    save: 'Save',
    cancel: 'Cancel',
    successTitle: 'Success',
    errorTitle: 'Error'
  },
  member: {
    detail: {
      title: 'Member Details',
      backShort: 'Back',
      backToList: 'Back to Members List',
      loadingData: 'Loading member data...',
      failedToLoad: 'Failed to load member data. Please try again.',
      notFound: 'Member not found',
      confirmDelete: 'Are you sure you want to delete {{name}}? This action cannot be undone.',
      successDeleteTitle: 'Success',
      successDeleteMsg: 'Member deleted successfully!',
      errorDeleteTitle: 'Error',
      errorDeleteMsg: 'Failed to delete member. Please try again.',
      personalInfo: 'Personal Information',
      gender: 'Gender:',
      dob: 'Date of Birth:',
      idNumber: 'ID Number:',
      nickname: 'Nickname:',
      maritalStatus: 'Marital Status:',
      occupation: 'Occupation:',
      contactInfo: 'Contact Information',
      mobile: 'Mobile:',
      email: 'Email:',
      address: 'Address:',
      membershipInfo: 'Membership Information',
      membershipType: 'Membership Status:',
      memberRole: 'Member Role:',
      joinDate: 'Join Date:',
      transferDate: 'Transfer Date:',
      baptismDate: 'Baptism Date:',
      baptismPlace: 'Baptism Church:',
      baptismNo: 'Member Code:',
      baptistPriest: 'Baptizing Pastor:',
      baptismClass: 'Baptism Class:',
      belongingDistrict: 'District:',
      belongingZone: 'Zone:',
      emergencyContact: 'Emergency Contact',
      contactName: 'Contact Name:',
      contactPhone: 'Contact Phone:',
      relationship: 'Relationship:',
      additionalNotes: 'Additional Notes',
      created: 'Created:',
      lastUpdated: 'Last Updated:',
      worshipNo: 'Worship No.',
      belongingGroup: 'Cell Group:',
      groupRole: 'Group Role:',
      breadcrumb: 'Member Management',
      roleRegular: 'Full Member',
      roleAssociate: 'Associate',
      roleTemporary: 'Temporary',
      statusActive: 'Active',
      statusInactive: 'Inactive',
      statusSuspended: 'Suspended'
    },
    tabs: {
      fullMember: 'Full Member',
      associate: 'Associate',
      temporary: 'Temporary'
    },
    upgrade: {
      button: 'Change Member Type',
      title: 'Change Member Type: {{name}}',
      memberType: 'Member Type',
      worshipId: 'Worship Number',
      worshipIdPlaceholder: 'Enter worship number',
      worshipIdRequired: 'Please enter a worship ID',
      confirm: 'Confirm',
      upgrading: 'Updating...',
      success: '{{name}} member type updated successfully!',
      error: 'Failed to update member type'
    },
    list: {
      title: 'Member Management',
      add: 'Add New Member',
      searchPlaceholder: 'Search by name, worship number, mobile, or email...',
      statusAll: 'All Status',
      statusActive: 'Active',
      statusInactive: 'Inactive',
      statusSuspended: 'Suspended',
      loading: 'Loading members...',
      error: 'Failed to load members. Please try again.',
      retry: 'Retry',
      table: {
        worship: 'Worship No.',
        nickname: 'Nickname',
        nameChi: 'Name (中文)',
        nameEng: 'Name (English)',
        gender: 'Gender',
        role: 'Member Role',
        group: 'Cell Group',
        zone: 'Zone',
        district: 'District',
        mobile: 'Mobile',
        email: 'Email',
        status: 'Status',
        joinDate: 'Join Date',
        actions: 'Actions'
      },
      empty: 'No members found.',
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete {{name}}?',
      deleteSuccess: 'Member deleted successfully!',
      deleteError: 'Failed to delete member. Please try again.',
      deleteModal: {
        title: 'Confirm Member Deletion',
        warning: 'Warning: This action cannot be undone!',
        message: 'You are about to permanently delete member "{{name}}" and all related records.',
        instruction: 'Please type "delete" to confirm deletion:',
        placeholder: 'Type delete',
        mismatch: 'Please type the correct confirmation text'
      },
      pagination: {
        showing: 'Showing {{from}} to {{to}} of {{total}} members',
        previous: 'Previous',
        next: 'Next',
        pageOf: 'Page {{page}} of {{total}}'
      },
      export: {
        button: 'Export All',
        success: 'Members exported successfully',
        error: 'Failed to export members'
      },
      template: {
        button: 'Download Template',
        downloaded: 'Template downloaded successfully',
        error: 'Failed to download template'
      },
      import: {
        button: 'Import All',
        importing: 'Importing...',
        success: 'Import completed: {{created}} created, {{updated}} updated, {{errors}} errors',
        error: 'Failed to import members',
        invalidFileType: 'Please select a CSV file',
        emptyFile: 'CSV file has no data',
        confirm: {
          title: 'Confirm Bulk Import',
          message: 'Please review the following before proceeding:',
          totalRows: 'Total rows',
          toCreate: 'New members to create',
          toUpdate: 'Existing members to update (by worship ID)',
          confirm: 'Confirm Import',
          cancel: 'Cancel'
        }
      }
    },
    form: {
      createTitle: 'Add New Member',
      editTitle: 'Edit "{{name}}"',
      cancelConfirm: 'Are you sure you want to cancel? Any unsaved changes will be lost.',
      successCreate: 'Member created successfully!',
      successUpdate: 'Member updated successfully!',
      save: 'Save',
      saving: 'Saving...',
      cancel: 'Cancel',
      showAdvanced: 'Show More Details (Address, Education, Organization, Health, etc.)',
      hideAdvanced: 'Hide More Details (Address, Education, Organization, Health, etc.)',
      showAdvancedShort: 'Show More Details',
      hideAdvancedShort: 'Hide Details',
      updateAction: 'Update Member',
      createAction: 'Create Member',
      sections: {
        basic: 'Basic Information',
        contact: 'Contact Information',
        address: 'Address',
        postalAddress: 'Postal Address',
        membership: 'Membership',
        education: 'Education',
        family: 'Family',
        occupation: 'Occupation',
        baptism: 'Baptism',
        religion: 'Religious Background',
        service: 'Service',
        cellGroup: 'Organization',
        health: 'Health',
        emergency: 'Emergency Contact',
        communication: 'Other Phones',
        remarks: 'Remarks',
        otherPhones: 'Other Phones (optional)'
      },
      labels: {
        worshipId: 'Worship ID',
        autoWorshipId: 'Auto-generate',
        octopusCardNumber: 'Octopus Card Number',
        status: 'Status *',
        chiName: 'Chinese Name *',
        engName: 'English Name',
        nickname: 'Nickname',
        gender: 'Gender',
        dob: 'Date of Birth',
        hkid: 'ID Number',
        nativePlace: 'Native Place',
        mobile: 'Mobile',
        email: 'Email',
        telDay: 'Tel (Day)',
        telNight: 'Tel (Night)',
        companyTel: 'Company Tel',
        fax: 'Fax',
        addr: 'Full Address',
        addrDistrict: 'District',
        region: 'Region',
        street: 'Street',
        estate: 'Estate',
        building: 'Building',
        unitNumber: 'Unit Number',
        postalAddr: 'Postal Address',
        statusActive: 'Active',
        statusInactive: 'Inactive',
        statusSuspended: 'Suspended',
        dateOfJoin: 'Join Date',
        dateOfJoinAdd: 'Additional Join Date',
        dateOfTransferAdd: 'Transfer Date',
        eduGrade: 'Education Level',
        eduName: 'School / Institution',
        marriedStatus: 'Marital Status',
        dateOfMarried: 'Date of Marriage',
        childQty: 'Number of Children',
        job: 'Occupation',
        income: 'Income',
        workRegion: 'Work Region',
        proQual: 'Professional Qualifications',
        dateOfBaptism: 'Baptism Date',
        baptismChurch: 'Baptism Church',
        baptismNo: 'Member Code',
        baptistPriest: 'Baptizing Pastor',
        baptismAppointment: 'Baptism Class / Cohort',
        religion: 'Religion',
        everChurch: 'Previous Church',
        believeDate: 'Date of Belief',
        servePosts: 'Service Roles',
        serveExp: 'Service Experience',
        cellGroup1: 'Group',
        cellGroup2: 'Zone (auto)',
        cellGroup3: 'District (auto)',
        cellGroup4: 'Other Group',
        selectGroup: 'Select group',
        health: 'Health',
        healthItems: 'Health Details',
        emergencyPerson: 'Emergency Contact',
        emergencyTel: 'Emergency Contact Tel',
        emergencyRelation: 'Relationship',
        receiveInfo: 'Receive Info',
        remark: 'Remark',
        remark2: 'Remark 2'
      },
      placeholders: {
        worshipId: 'e.g., 170074 or T170074',
        octopusCardNumber: 'e.g., 012-345-678-9',
        statusFormat: 'Format: Regular member (170074) or Temporary (T170074)',
        emergencyRelation: 'e.g., Spouse, Parent, Sibling',
        remark: 'Any additional notes or comments...',
        eduGrade: 'e.g., Secondary School, University, Master',
        eduName: 'e.g., ABC Secondary School / XYZ University'
      },
      hints: {
        addressStructured: 'Fill in the address fields below; the full address is composed automatically.',
        groupAutoOrg: 'Selecting a group auto-fills zone and district.',
        zoneLeaderOrg: 'This member is a zone leader; group shows as NA.',
        districtPastorOrg: 'This member is a district pastor; group and zone show as NA.',
        otherPhones: 'Day/night phones are optional and separate from mobile/email above.'
      },
      options: {
        genderSelect: 'Select Gender',
        genderMale: 'Male',
        genderFemale: 'Female',
        genderOther: 'Other',
        maritalSelect: 'Select Status',
        maritalSingle: 'Single',
        maritalMarried: 'Married',
        maritalDivorced: 'Divorced',
        maritalWidowed: 'Widowed'
      },
      errors: {
        load: 'Failed to load member data. Please try again.',
        save: 'Failed to save member. Please try again.'
      }
    }
  },
  access: { deniedTitle: 'Access Denied', deniedDescription: "You don't have permission to access this section." },
  admin: {
    secureNotice: 'Secure Admin Area - All API calls protected with Bearer authentication',
    headerTitle: 'Admin Management',
    headerSubtitle: 'Manage administrators and their permissions',
    list: {
      addNewAdmin: 'Add New Admin',
      errorLoading: 'Error loading admins',
      searchPlaceholder: 'Search admins...',
      perPage: '{{count}} per page',
      table: {
        admin: 'Admin',
        email: 'Email',
        status: 'Status',
        permissions: 'Permissions',
        created: 'Created',
        actions: 'Actions',
        active: 'Active',
        inactive: 'Inactive',
        permissionsCount: '{{count}} permission(s)',
        modulesCount: '{{count}} module(s)'
      },
      empty: 'No admins found.',
      emptySearch: 'No admins found matching your search.',
      manage: 'Manage',
      delete: 'Delete',
      confirmDelete: 'Are you sure you want to delete this admin?',
      notifications: {
        deleteSuccess: 'Admin deleted successfully',
        deleteError: 'Failed to delete admin'
      },
      pagination: {
        showing: 'Showing {{from}} to {{to}} of {{total}} admins',
        previous: 'Previous',
        next: 'Next'
      }
    },
    export: {
      button: 'Export All',
      tooltip: 'Export all admins to CSV file',
      success: 'Admins exported successfully',
      error: 'Failed to export admins'
    },
    template: {
      button: 'Download Template',
      tooltip: 'Download CSV import template',
      downloaded: 'Template downloaded successfully',
      error: 'Failed to download template'
    },
    import: {
      button: 'Import All',
      tooltip: 'Import admins from CSV file',
      importing: 'Importing...',
      success: 'Import completed: {{created}} created, {{updated}} updated, {{errors}} errors',
      error: 'Failed to import admins',
      invalidFileType: 'Please select a CSV file',
      emptyFile: 'CSV file has no data',
      confirm: {
        title: 'Confirm Bulk Import',
        message: 'Please review the following before proceeding:',
        totalRows: 'Total rows',
        toCreate: 'New admins to create',
        toUpdate: 'Existing admins to update',
        confirm: 'Confirm Import',
        cancel: 'Cancel'
      }
    },
    form: {
      title: 'Create New Admin',
      username: 'Username',
      email: 'Email',
      firstName: 'First Name',
      lastName: 'Last Name',
      password: 'Password',
      passwordPlaceholder: 'Enter secure password (min 6 characters)',
      usernamePlaceholder: 'Enter username',
      emailPlaceholder: 'Enter email address',
      firstNamePlaceholder: 'First name',
      lastNamePlaceholder: 'Last name',
      active: 'Active (user can log in)',
      cancel: 'Cancel',
      create: 'Create Admin',
      creating: 'Creating...',
      notifications: {
        createSuccess: 'Admin created successfully',
        createError: 'Failed to create admin'
      }
    },
    modal: {
      title: 'Manage Admin - {{username}}',
      tabs: {
        edit: 'Edit Admin',
        permissions: 'Access Control'
      },
      info: {
        title: 'Access Control Information',
        description: 'Configure which modules this admin can access and what actions they can perform.'
      },
      fields: {
        username: 'Username',
        email: 'Email',
        firstName: 'First Name',
        lastName: 'Last Name',
        active: 'Active'
      },
      permissions: {
        create: 'Create',
        read: 'Read',
        update: 'Update',
        delete: 'Delete',
        manage: 'Manage'
      },
      modules: {
        dashboard: 'Dashboard',
        admins: 'Admin Management',
        members: 'Member Management',
        events: 'Event Management',
        attendance_records: 'Attendance Records',
        attendance_checkin: 'Attendance Check-in',
        tithe_receipts: 'Tithe Receipt Management',
        forms: 'Form Management',
        posts: 'Post Management',
        joomla: 'Joomla Integration'
      },
      noChangesTitle: 'No Changes',
      noChangesMsg: 'No changes were made to update.',
      save: 'Save Changes',
      saving: 'Saving...',
      cancel: 'Cancel',
      notifications: {
        updateSuccess: 'Admin updated successfully',
        updateError: 'Failed to update admin',
        permsSuccess: 'Permissions updated successfully',
        permsError: 'Failed to update permissions'
      }
    }
  },
  tithe: {
    secureNotice: 'Secure Tithe Management - Bearer token authentication enabled',
    title: 'Tithe Receipt Management',
    refresh: 'Refresh',
    searchPlaceholder: 'Search by name, phone, worship code, bank...',
    filters: {
      statusAll: 'All Status',
      statusPending: 'Pending',
      statusApproved: 'Approved',
      statusRejected: 'Rejected',
      yearAll: 'All Years',
      monthAll: 'All Months',
      month01: 'January',
      month02: 'February',
      month03: 'March',
      month04: 'April',
      month05: 'May',
      month06: 'June',
      month07: 'July',
      month08: 'August',
      month09: 'September',
      month10: 'October',
      month11: 'November',
      month12: 'December',
      perPage: '{{count}} per page'
    },
    stats: {
      monthlyTitle: 'This Month ({{month}})',
      allTime: 'All Time',
      receipts: 'Receipts',
      pending: 'Pending',
      approved: 'Approved',
      totalAmount: 'Total Amount'
    },
    table: {
      receiptInfo: 'Receipt Info',
      contact: 'Contact',
      payment: 'Payment Details',
      amount: 'Amount',
      status: 'Status',
      actions: 'Actions',
      worship: 'Worship Code:',
      createdAt: 'Created:',
      emailMissing: 'No email',
      statusLabel: {
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected'
      },
      view: 'View',
      approve: 'Approve',
      reject: 'Reject',
      download: 'Download',
      breakdown: {
        tithe: 'Tithe',
        building: 'Building',
        thanksgiving: 'Thanksgiving',
        loveFund: 'Love Fund'
      }
    },
    pagination: {
      showing: 'Showing {{from}} to {{to}} of {{total}} results',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {{page}} of {{total}}'
    },
    empty: {
      none: 'No tithe receipts found',
      adjust: 'Try adjusting your search criteria'
    },
    notifications: {
      fetchError: 'Failed to fetch tithe receipts',
      statsError: 'Failed to fetch statistics',
      statusUpdated: 'Receipt {{status}} successfully',
      statusError: 'Failed to update status',
      receiptError: 'Failed to fetch receipt details',
      downloadStarted: 'Receipt file download started',
      downloadError: 'Failed to download receipt file'
    },
    modal: {
      title: 'Tithe Receipt #{{id}}',
      status: 'Status',
      created: 'Created:',
      approve: 'Approve',
      reject: 'Reject',
      close: 'Close',
      approveTitle: 'Approve Receipt',
      approveConfirm: 'Are you sure you want to approve this tithe receipt?',
      approveRemarks: 'Approval Remarks (Optional):',
      approveSubmit: 'Approve',
      rejectTitle: 'Reject Receipt',
      rejectConfirm: 'Please provide a reason for rejecting this tithe receipt:',
      rejectReason: 'Rejection Reason:',
      rejectSubmit: 'Reject',
      cancel: 'Cancel',
      personalInfo: 'Personal Information',
      addressInfo: 'Address Information',
      paymentInfo: 'Payment Information',
      donationBreakdown: 'Donation Breakdown',
      additionalInfo: 'Additional Information',
      fields: {
        chineseName: 'Chinese Name:',
        englishName: 'English Name:',
        contactPhone: 'Contact Phone:',
        email: 'Email:',
        worshipCode: 'Worship Code:',
        address: 'Address:',
        district: 'District:',
        area: 'Area/Country:',
        receivingBank: 'Receiving Bank:',
        transactionType: 'Transaction Type:',
        depositDate: 'Deposit Date:',
        referenceNumber: 'Reference Number:',
        tithe: 'Tithe Amount (當納十分一):',
        thanksgiving: 'Thanksgiving Offering (感恩奉獻):',
        churchOperating: 'Church Operating (教會經常費):',
        buildingFund: 'Building Fund (建堂基金):',
        gospelMinistry: 'Gospel Ministry (福音事工):',
        loveNeighbor: 'Love Neighbor Fund (愛鄰舍基金):',
        specialOffering: 'Special Offering (特別奉獻):',
        specialOfferingDesc: 'Special Offering Description:',
        totalAmount: 'Total Amount:',
        receiptRequired: 'Receipt Required:',
        languagePreference: 'Language Preference:',
        uploadedReceipt: 'Uploaded Receipt:',
        remarks: 'Remarks:',
        processedBy: 'Processed By:',
        processedAt: 'Processed At:',
        yes: 'Yes',
        no: 'No'
      }
    }
  },
  events: {
    secureNotice: 'Secure Events Area - Bearer token authentication enabled',
    title: 'Events Management',
    tabs: {
      worship: 'Regular Worship',
      course: 'Course',
      cellGroup: 'Cell Group',
      meeting: 'Meeting',
      event: 'Event'
    },
    table: {
      id: 'ID',
      eventName: 'Event Name',
      venue: 'Venue',
      lecturer: 'Lecturer',
      sections: 'Sections',
      nextSection: 'Next Section',
      created: 'Created',
      actions: 'Actions',
      noEvents: 'No events found. Create your first event!',
      noMatch: 'No events found matching your search.'
    },
    actions: {
      manage: 'Manage',
      duplicate: 'Duplicate',
      delete: 'Delete',
      create: 'Create {{type}}'
    },
    filters: {
      searchPlaceholder: 'Search {{type}}...',
      searchWorships: 'Search worship services...',
      searchCourses: 'Search courses...',
      searchCellGroups: 'Search cell groups...',
      searchMeetings: 'Search meetings...',
      searchEvents: 'Search events...',
      from: 'From:',
      to: 'To:',
      clearFilter: 'Clear date filter',
      perPage: '{{count}} per page'
    },
    pagination: {
      showing: 'Showing {{from}} to {{to}} of {{total}} events',
      previous: 'Previous',
      next: 'Next'
    },
    loading: 'Loading events...',
    error: 'Error loading events',
    retry: 'Retry',
    management: {
      worship: 'Regular Worship Management',
      course: 'Courses Management',
      cellGroup: 'Cell Groups Management',
      meeting: 'Meetings Management',
      event: 'Events Management'
    },
    modal: {
      createTitle: 'Create New Event',
      editTitle: 'Edit Event',
      eventInfo: 'Event Information',
      eventName: 'Event Name *',
      eventNamePlaceholder: 'Enter event name',
      eventType: 'Event Type *',
      venue: 'Venue',
      venuePlaceholder: 'Enter venue',
      mainLecturer: 'Main Lecturer',
      mainLecturerPlaceholder: 'Enter main lecturer name(s)',
      mainLecturerNote: '(if not specified in sections)',
      defaultQuantity: 'Default Quantity',
      defaultQuantityPlaceholder: 'Enter default quantity',
      defaultQuantityNote: '(applies to all sections unless overridden)',
        attachForm: 'Attach Form',
        attachFormNone: '-- None --',
        attachFormEdit: 'Edit',
        attachFormResults: 'Results',
        remark: 'Remark',
      remarkPlaceholder: 'Enter remarks or additional notes',
      sectionsTitle: 'Sections/Classes',
      addSection: 'Add Section',
      sectionName: 'Section Name (Optional)',
      sectionNamePlaceholder: 'e.g., Morning Session, Class A',
      lecturer: 'Lecturer(s)',
      lecturerPlaceholder: 'e.g., John Doe, Jane Smith',
      quantity: 'Quantity',
      quantityPlaceholder: 'Enter quantity',
      startTime: 'Start Time *',
      startTimePlaceholder: 'Select start date & time',
      endTime: 'End Time *',
      endTimePlaceholder: 'Select end date & time',
      dragHandle: 'Drag to reorder',
      removeSection: 'Remove Section',
      noSections: 'At least one section is required',
      errors: {
        eventNameRequired: 'Event name is required',
        startTimeRequired: 'Start time is required',
        endTimeRequired: 'End time is required',
        endTimeAfterStart: 'End time must be after start time'
      },
      buttons: {
        save: 'Save Changes',
        saving: 'Saving...',
        create: 'Create Event',
        cancel: 'Cancel'
      }
    },
    confirmations: {
      deleteTitle: 'Delete Event?',
      deleteMessage: 'Are you sure you want to delete "{{name}}"? This will also delete all associated sections.',
      duplicateTitle: 'Duplicate Event?',
      duplicateMessage: 'Are you sure you want to duplicate "{{name}}"? A copy will be created with "(Copy)" appended to the name.',
      noChangesTitle: 'No Changes',
      noChangesMsg: 'No changes were made to update.'
    },
    notifications: {
      createSuccess: 'Event created successfully',
      updateSuccess: 'Event updated successfully',
      duplicateSuccess: 'Event duplicated successfully',
      deleteSuccess: 'Event deleted successfully',
      error: 'Failed to {{action}} event'
    },
    eventTypes: {
      worship: 'Regular Worship 常規崇拜',
      course: 'Course 課程',
      cellGroup: 'Cell Group 小組',
      meeting: 'Meeting 會議',
      event: 'Event 活動'
    }
  },
  dashboard: {
    secureNotice: 'Secure Admin Dashboard - Bearer token authentication enabled',
    welcomeTitle: 'Welcome back, {{name}}! 👋',
    welcomeSubtitle: "Here's what's happening with your CCBC platform today.",
    vsLastMonth: 'vs last month',
    recentActivitiesTitle: 'Recent Activities',
    quickActions: 'Quick Actions',
    systemStatus: 'System Status',
    stats: {
      totalAdmins: 'Total Admins',
      platformPosts: 'Platform Posts',
      joomlaArticles: 'Joomla Articles',
      activeSessions: 'Active Sessions'
    },
    navDebug: {
      title: 'Navigation Debug',
      currentUser: 'Current User:',
      testLinks: 'Test Direct Links (Click these to test navigation):',
      hint: "If these links don't work, check browser console for errors."
    },
    activities: {
      login: 'Admin logged in',
      create: 'New post created',
      update: 'Admin profile updated',
      system: 'System backup completed'
    },
    time: {
      justNow: 'Just now',
      twoHoursAgo: '2 hours ago',
      oneDayAgo: '1 day ago',
      twoDaysAgo: '2 days ago'
    },
    actions: {
      addAdmin: 'Add Admin',
      newPost: 'New Post',
      viewStats: 'View Stats',
      settings: 'Settings'
    },
    status: {
      backend: 'FastAPI Backend',
      database: 'MySQL Database',
      cms: 'Joomla CMS',
      operational: 'Operational',
      connected: 'Connected',
      accessible: 'Accessible'
    }
  },
  titheReceipts: {
    secureNotice: 'Secure Tithe Management - Bearer token authentication enabled',
    headerTitle: 'Tithe Receipt Management',
    headerSubtitle: 'Manage tithe receipts and donation records',
    createNew: 'Create New Receipt',
    refresh: 'Refresh',
    form: {
      createTitle: 'Create New Tithe Receipt',
      personalInfo: 'Personal Information',
      addressInfo: 'Address Information',
      paymentInfo: 'Payment Information',
      donationBreakdown: 'Donation Breakdown',
      receiptOptions: 'Receipt Options',
      uploadReceipt: 'Upload Receipt File',
      remarks: 'Remarks',
      chineseName: 'Chinese Name',
      englishName: 'English Name',
      contactPhone: 'Contact Phone',
      email: 'Email',
      worshipCode: 'Worship Code',
      address: 'Address',
      district: 'District',
      area: 'Area',
      receivingBank: 'Receiving Bank',
      transactionType: 'Transaction Type',
      depositDate: 'Deposit Date',
      referenceNumber: 'Reference Number',
      tithe: 'Tithe',
      thanksgiving: 'Thanksgiving Offering',
      churchOperating: 'Church Operating',
      buildingFund: 'Building Fund',
      gospelMinistry: 'Gospel Ministry',
      loveNeighbor: 'Love Neighbor Fund',
      specialOffering: 'Special Offering',
      specialOfferingDesc: 'Special Offering Description',
      otherOffering: 'Other',
      otherOfferingDesc: 'Other (please specify)',
      otherOfferingPlaceholder: 'Please specify the other offering purpose…',
      totalAmount: 'Total Amount',
      receiptRequired: 'Receipt Required',
      receiptType: 'Receipt Type',
      receiptIndependent: 'Individual Receipt',
      receiptYearly: 'Annual Receipt',
      receiptLanguage: 'Receipt Language',
      selectFile: 'Receipt File (PDF, JPEG, PNG - Max 5MB)',
      selectedFile: 'Selected: {{name}}',
      create: 'Create Receipt',
      creating: 'Creating…',
      cancel: 'Cancel',
      createdSuccess: 'Tithe receipt #{{id}} created successfully',
      createError: 'Failed to create tithe receipt',
      validationRequired: 'Chinese name and contact phone are required',
      validationAmount: 'At least one donation amount must be greater than 0'
    },
    stats: {
      thisMonth: "This Month ({{month}})",
      allTime: 'All Time',
      receipts: 'Receipts',
      pending: 'Pending',
      approved: 'Approved',
      totalAmount: 'Total Amount'
    },
    controls: {
      searchPlaceholder: 'Search by name, phone, worship code, bank...',
      status: {
        all: 'All Status',
        pending: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected'
      },
      year: { all: 'All Years' },
      month: {
        all: 'All Months',
        m01: 'January', m02: 'February', m03: 'March', m04: 'April', m05: 'May', m06: 'June',
        m07: 'July', m08: 'August', m09: 'September', m10: 'October', m11: 'November', m12: 'December'
      },
      perPage: '{{count}} per page'
    },
    table: {
      receiptInfo: 'Receipt Info',
      contact: 'Contact',
      paymentDetails: 'Payment Details',
      amount: 'Amount',
      status: 'Status',
      actions: 'Actions',
      worship: 'Worship Code',
      noEmail: 'No email'
    },
    actions: {
      view: 'View',
      approve: 'Approve',
      reject: 'Reject',
      download: 'Download'
    },
    statuses: { pending: 'Pending', approved: 'Approved', rejected: 'Rejected' },
    pagination: {
      showing: 'Showing {{from}} to {{to}} of {{total}} results',
      previous: 'Previous',
      next: 'Next',
      pageOf: 'Page {{page}} of {{total}}'
    },
    empty: {
      noResults: 'No tithe receipts found',
      tryAdjustSearch: 'Try adjusting your search criteria'
    },
    notifications: {
      fetchError: 'Failed to fetch tithe receipts',
      statsError: 'Failed to fetch statistics',
      statusUpdatedTitle: 'Status Updated',
      statusUpdatedMsg: 'Receipt {{status}} successfully',
      downloadStartedTitle: 'Download Started',
      downloadStartedMsg: 'Receipt file download started',
      downloadErrorTitle: 'Download Error',
      downloadErrorMsg: 'Failed to download receipt file'
    },
    modal: {
      title: 'Tithe Receipt #{{id}}',
      status: 'Status:',
      created: 'Created:',
      close: 'Close',
      approveTitle: 'Approve Receipt',
      approveConfirm: 'Are you sure you want to approve this tithe receipt?',
      approveRemarks: 'Approval Remarks (Optional):',
      approveAction: 'Approve',
      rejectTitle: 'Reject Receipt',
      rejectPrompt: 'Please provide a reason for rejecting this tithe receipt:',
      rejectReason: 'Rejection Reason:',
      rejectPlaceholder: 'Please explain why this receipt is being rejected...',
      rejectAction: 'Reject',
      cancel: 'Cancel',
      sections: {
        personal: 'Personal Information',
        address: 'Address Information',
        payment: 'Payment Information',
        donation: 'Donation Breakdown',
        additional: 'Additional Information'
      },
      fields: {
        chineseName: 'Chinese Name:',
        englishName: 'English Name:',
        contactPhone: 'Contact Phone:',
        email: 'Email:',
        worshipCode: 'Worship Code:',
        address: 'Address:',
        district: 'District:',
        area: 'Area/Country:',
        receivingBank: 'Receiving Bank:',
        transactionType: 'Transaction Type:',
        depositDate: 'Deposit Date:',
        referenceNumber: 'Reference Number:',
        titheAmount: 'Tithe Amount (當納十分一):',
        thanksgiving: 'Thanksgiving Offering (感恩奉獻):',
        churchOperating: 'Church Operating (教會經常費):',
        buildingFund: 'Building Fund (建堂基金):',
        gospelMinistry: 'Gospel Ministry (福音事工):',
        loveNeighbor: 'Love Neighbor Fund (愛鄰舍基金):',
        specialOffering: 'Special Offering (特別奉獻):',
        specialOfferingDesc: 'Special Offering Description:',
        otherOffering: 'Other:',
        otherOfferingDesc: 'Other (please specify):',
        totalAmount: 'Total Amount:',
        receiptRequired: 'Receipt Required:',
        receiptType: 'Receipt Type:',
        receiptIndependent: 'Individual Receipt',
        receiptYearly: 'Annual Receipt',
        yes: 'Yes',
        no: 'No',
        languagePreference: 'Language Preference:',
        uploadedReceipt: 'Uploaded Receipt:',
        remarks: 'Remarks:',
        processedBy: 'Processed By:',
        processedAt: 'Processed At:'
      }
    }
  },
  organization: {
    title: 'Organization Structure',
    subtitle: 'District → Zone → Group',
    legend: {
      district: 'District',
      zone: 'Zone',
      group: 'Group'
    },
    loading: 'Loading organization data...',
    error: 'Failed to load organization data.',
    reload: 'Reload',
    addFirst: 'Add First Area',
    empty: 'No organization structure yet',
    filters: {
      districtLabel: 'District:',
      zoneLabel: 'Zone:',
      groupLabel: 'Group:',
      districtPlaceholder: 'Search district...',
      zonePlaceholder: 'Search zone...',
      groupPlaceholder: 'Search group...',
      districtEmpty: 'No districts found',
      zoneEmpty: 'No zones found',
      groupEmpty: 'No groups found',
      clearAll: 'Clear filters'
    },
    actions: {
      addDistrict: 'Add District',
      addZone: 'Add Zone',
      addGroup: 'Add Group',
      manageMembers: 'Manage Members',
      edit: 'Edit',
      delete: 'Delete',
      groupCount: '{{count}} groups',
      zoneCount: '{{count}} zones'
    },
    badges: {
      district: 'District',
      zone: 'Zone',
      group: 'Group'
    },
    detail: {
      districtManager: 'District Pastor:',
      zoneManager: 'Zone Leader:',
      groupManager: 'Group Leader:',
      noneAssigned: 'Unassigned'
    },
    stats: {
      core: 'Core Members',
      full: 'Full Members',
      associate: 'Associate Members',
      total: 'Total'
    },
    confirmDelete: 'Are you sure you want to delete this {{type}}?',
    confirmRemoveMember: 'Remove this member?',
    deleteError: 'Failed to delete. Please try again later.',
    saveError: 'Failed to save. Please try again later.',
    duplicateName: '{{type}} "{{name}}" already exists. Please use another name.',
    emptyName: '{{type}} name cannot be empty',
    removeError: 'Failed to remove member.',
    types: {
      district: 'District',
      zone: 'Zone',
      group: 'Group',
      districtManager: 'District Pastor',
      zoneManager: 'Zone Leader',
      groupManager: 'Group Leader'
    },
    modal: {
      createTitle: 'Create ',
      editTitle: 'Edit ',
      parentLabel: 'Parent {{parent}}',
      nameLabel: '{{type}} Name *',
      namePlaceholder: 'e.g. {{type}} name',
      managerLabel: '{{manager}}',
      loadingMembers: 'Loading members...',
      searchManager: 'Search {{manager}} name or ID, or enter name directly...',
      managerHint: 'Select a member or enter a non-member name',
      moreMembers: '{{count}} more members...',
      noMembers: 'No matching members',
      description: 'Description',
      descriptionPlaceholder: 'Optional',
      cancel: 'Cancel',
      save: 'Save'
    },
    membersModal: {
      title: '{{group}} Members',
      summaryCore: 'Core: {{count}}',
      summaryFull: 'Full: {{count}}',
      summaryAssociate: 'Associate: {{count}}',
      addLabel: 'Add Member',
      searchPlaceholder: 'Search member name or ID...',
      loadingMembers: 'Loading members...',
      moreMembers: '{{count}} more members...',
      noMembers: 'No matching members',
      roleCore: 'Core Member',
      roleFull: 'Full Member',
      roleAssociate: 'Associate Member',
      addButton: 'Add',
      loadingAssignments: 'Loading...',
      empty: 'No members yet',
      worshipId: 'Worship ID: {{id}}',
      worshipMissing: 'Worship ID not provided',
      remove: 'Remove',
      saveAll: 'Save Changes',
      close: 'Close',
      addSuccess: 'Member added successfully',
      addError: 'Failed to add member',
      roleSuccess: 'Roles updated successfully',
      roleError: 'Failed to update roles',
      removeError: 'Failed to remove member',
      coreForbiddenForLeader: 'District pastors, zone leaders, and group leaders cannot be core members'
    }
  },
  attendance: {
    secureNotice: 'Secure Attendance Area - Bearer token authentication enabled',
    title: 'Attendance Records',
    tabs: {
      worship: 'Regular Worship',
      course: 'Course',
      cellGroup: 'Cell Group',
      meeting: 'Meeting',
      event: 'Event'
    },
    header: {
      regularWorship: 'Regular Worship',
      course: 'Course',
      cellGroup: 'Cell Group',
      meeting: 'Meeting',
      events: 'Events',
      attendanceRecords: 'Attendance Records'
    },
    filters: {
      from: 'From:',
      to: 'To:',
      clearDateFilter: 'Clear date filter',
      eventType: 'Event Type',
      selectEvent: 'Select Event',
      selectSection: 'Select Section',
      perPage: '{{count}} per page',
      search: 'Search by member name or worship ID...'
    },
    memberFilter: {
      title: 'All Attendance Records',
      subtitle: 'List records across all event types with filters',
      filtersApplied: '{{count}} filters active',
      clearAll: 'Clear all filters',
      memberLabel: 'Member',
      memberPlaceholder: 'Type 2+ characters to search members...',
      memberSearchError: 'Unable to load member suggestions right now',
      clearMember: 'Clear member',
      selected: 'Selected: {{member}}',
      resultSummary: '{{count}} records · {{dateRange}} · {{member}}',
      noDateRange: 'no date range',
      anyone: 'any member',
      unknownMember: 'Unknown member',
      excused: 'Excused',
      notAttended: 'Not attended',
      attended: 'Attended',
    },
    table: {
      worshipId: 'Worship ID',
      mobilePhone: 'Mobile Phone',
      memberName: 'Member Name',
      event: 'Event',
      section: 'Section',
      checkInTime: 'Check-in Time',
      qrTime: 'QR Time',
      status: 'Status',
      actions: 'Actions'
    },
    actions: {
      view: 'View',
      edit: 'Edit',
      delete: 'Delete',
      createRecord: 'Create Record'
    },
    status: {
      present: 'Present',
      absent: 'Absent',
      late: 'Late'
    },
    confirmDelete: 'Are you sure you want to delete attendance record for Worship ID {{id}}?',
    loading: 'Loading attendance records...',
    error: 'Error loading attendance records',
    noRecords: 'No attendance records found',
    notifications: {
      deleteSuccess: 'Attendance record deleted successfully',
      deleteError: 'Failed to delete attendance record',
      updateSuccess: 'Attendance record updated successfully',
      updateError: 'Failed to update attendance record'
    },
    pagination: {
      showing: 'Showing {{from}} to {{to}} of {{total}} records',
      previous: 'Previous',
      next: 'Next'
    },
    modal: {
      title: 'Attendance Record',
      close: 'Close'
    }
  },
  checkIn: {
    secureNotice: 'Secure Check-in Area - Bearer token authentication enabled',
    title: 'Check In (QR Scan)',
    selectEvent: 'Select Event',
    selectSection: 'Select Section',
    noEvents: 'No events available',
    noSections: 'No sections available',
    scannerType: 'Scanner Type',
    scannerTypeUniversal: 'Universal Scanner',
    scannerTypeYudiel: 'Yudiel QR Scanner',
    startScanning: 'Start Scanning',
    stopScanning: 'Stop Scanning',
    keepRunning: 'Keep Scanner Running',
    manualInput: 'Manual Input',
    enterQRCode: 'Enter QR Code or Worship ID',
    submit: 'Submit',
    diagnostics: 'Show Diagnostics',
    recentScans: 'Recent Scans',
    scanResult: 'Scan Result',
    noScans: 'No scans yet',
    error: 'Error',
    success: 'Success',
    loading: 'Loading...',
    selectEventSection: 'Select Event & Section',
    qrCodeScanner: 'QR Code Scanner',
    checkInAttendance: 'Check In (QR Scan)',
    scanInstructions: 'Select an event and section, then scan QR codes to check in attendance.',
    notifications: {
      checkInSuccess: 'Check-in successful',
      checkInError: 'Check-in failed',
      invalidQR: 'Invalid QR code',
      worshipNotFound: 'Worship ID not found',
      selectionRequired: 'Selection Required',
      selectionMessage: 'Please select both an event and a section before starting to scan.'
    },
    messages: {
      selectEventFirst: 'Please select an event first',
      selectSectionFirst: 'Please select a section first',
      scanningStarted: 'Scanning started',
      scanningStopped: 'Scanning stopped',
      cameraError: 'Camera access error',
      cameraNotFound: 'Camera not found'
    }
  },
  continuousCheckIn: {
    title: 'Continuous Check-in',
    description: 'Scan multiple QR codes one by one without stopping',
    selectEvent: 'Select Event',
    selectSection: 'Select Section',
    startScanning: 'Start Scanning',
    stopScanning: 'Stop Scanning',
    scannedCount: 'Scanned: {{count}}',
    totalRecords: 'Total Records',
    noRecords: 'No scans yet',
    messages: {
      selectEventFirst: 'Please select an event first',
      selectSectionFirst: 'Please select a section first',
      scanningStarted: 'Scanning started',
      scanningStopped: 'Scanning stopped'
    },
    notifications: {
      checkInSuccess: 'Check-in successful',
      checkInError: 'Check-in failed',
      invalidQR: 'Invalid QR code'
    }
  },
  forms: {
    list: {
      title: 'Form Management',
      create: 'Create Form',
      searchPlaceholder: 'Search forms...',
      perPage: 'Show {{count}}',
      table: {
        formName: 'Form Name',
        slug: 'Slug',
        status: 'Status',
        actions: 'Actions'
      },
      actions: {
        edit: 'Edit',
        submissions: 'Submissions',
        view: 'View',
        delete: 'Delete'
      },
      status: {
        active: 'active',
        draft: 'draft',
        archived: 'archived'
      },
      empty: 'No forms found.',
      emptySearch: 'No forms found matching your search.',
      pagination: {
        showing: 'Showing {{from}} to {{to}} of {{total}} forms',
        previous: 'Previous',
        next: 'Next'
      },
      confirmDelete: 'This will permanently delete the form "{{name}}" and all submissions. Continue?',
      deleteError: 'Failed to drop form'
    },
    builder: {
      createTitle: 'Create Form',
      editTitle: 'Edit Form',
      errorLoading: 'Error Loading Form',
      labels: {
        name: 'Name',
        slug: 'Slug',
        description: 'Description',
        status: 'Status',
        openAt: 'Open At',
        closeAt: 'Close At'
      },
      placeholders: {
        openAtNote: 'Leave blank to open immediately.',
        closeAtNote: 'Leave blank to never auto-close.'
      },
      statusOptions: {
        draft: 'Draft',
        published: 'Published',
        archived: 'Archived'
      },
      tabs: {
        builder: 'Visual Builder',
        json: 'JSON'
      },
      palette: {
        title: 'Add fields',
        formTitle: 'Form Title',
        formTitleEn: 'Form Title (EN)',
        formTitleTc: 'Form Title (中文)'
      },
      canvas: {
        dragInstruction: 'Drag to reorder fields',
        empty: 'No fields yet. Add from left.'
      },
      inspector: {
        title: 'Field Settings',
        empty: 'Select a field to edit',
        labels: {
          label: 'Label',
          labelEn: 'Label (EN)',
          labelTc: 'Label (中文)',
          key: 'Key',
          type: 'Type',
          required: 'Required',
          placeholder: 'Placeholder (EN)',
          placeholderTc: 'Placeholder (中文)',
          options: 'Options (comma separated)',
          optionsEn: 'Options EN (comma separated)',
          optionsTc: 'Options 中文 (comma separated)'
        },
        removeField: 'Remove Field'
      },
      fieldTypes: {
        text: 'Text',
        email: 'Email',
        number: 'Number',
        date: 'Date',
        textarea: 'Textarea',
        select: 'Select',
        multiselect: 'Multi Select',
        radio: 'Radio',
        checkbox: 'Checkbox',
        file: 'File'
      },
      jsonNote: 'Structure: { title: string, fields: [{ key, label, type, required?, options? }] }',
      invalidJson: 'Invalid JSON',
      buttons: {
        save: 'Save Form',
        saving: 'Saving...',
        cancel: 'Cancel'
      },
      errors: {
        failedToLoad: 'Failed to load form',
        failedToSave: 'Failed to save form'
      }
    },
    submissions: {
      title: 'Form Submissions',
      exportCsv: 'Export CSV',
      dropForm: 'Drop Form',
      searchPlaceholder: 'Search submissions...',
      perPage: 'Show {{count}}',
      table: {
        id: 'ID',
        created: 'Created',
        submitterEmail: 'Submitter Email',
        noEmail: '-'
      },
      empty: 'No submissions yet',
      emptySearch: 'No submissions found matching your search.',
      pagination: {
        showing: 'Showing {{from}} to {{to}} of {{total}} submissions',
        previous: 'Previous',
        next: 'Next'
      },
      confirmDelete: 'This will permanently delete the form "{{name}}" and all submissions. Continue?',
      exportError: 'Failed to export',
      deleteError: 'Failed to drop form',
      errorLoading: 'Error Loading Submissions',
      loadError: 'Failed to load submissions'
    }
  },
  titheSubmission: {
    title: 'Tithe Submission',
    subtitle: 'Submit your tithe offering to the church',
    successMessage: 'Thank you! Your tithe submission has been received. We will process it shortly.',
    personalInfo: 'Personal Information',
    paymentInfo: 'Payment Information',
    donationAmounts: 'Donation Amounts',
    additionalInfo: 'Additional Information',
    totalAmount: 'Total Amount',
    selectBank: 'Select receiving bank',
    selectTransactionType: 'Select transaction type',
    submit: 'Submit Tithe',
    submitting: 'Submitting...',
    fields: {
      chineseName: 'Chinese Name',
      worshipCode: 'Worship Code',
      worshipCodeOptional: '(Optional - leave blank if not a member)',
      contactPhone: 'Contact Phone',
      email: 'Email Address',
      receivingBank: 'Receiving Bank',
      transactionType: 'Transaction Type',
      depositDate: 'Deposit Date & Time',
      tithe: 'Tithe Amount',
      thanksgiving: 'Thanksgiving Offering',
      churchOperating: 'Church Operating Fund',
      buildingFund: 'Building Fund',
      gospelMinistry: 'Gospel Ministry',
      loveNeighbor: 'Love Neighbor Fund',
      specialOffering: 'Special Offering',
      specialOfferingDesc: 'Special Offering Description',
      receiptRequired: 'I need a tithe receipt',
      receiptFrequency: 'Receipt preference',
      receiptFrequencyPerSubmission: 'Issue receipt per submission',
      receiptFrequencyYearly: 'Issue one consolidated yearly receipt',
      receiptFrequencyHint: 'Choose how you would like to receive receipts.',
      remarks: 'Additional Remarks',
      remarksPlaceholder: 'Please provide any additional information or special instructions here...',
      uploadReceipt: 'Upload receipt (PDF/JPG/PNG)',
      uploadHint: 'Optional. Accepted formats: PDF, JPG, PNG.'
    },
    errors: {
      nameRequired: 'Please enter your name',
      phoneRequired: 'Please enter your contact phone number',
      bankRequired: 'Please select a receiving bank',
      transactionTypeRequired: 'Please select a transaction type',
      depositDateRequired: 'Please select the deposit date and time',
      amountRequired: 'Please enter at least one donation amount',
      invalidFileType: 'Only PDF, JPG or PNG files are allowed',
      uploadFailed: 'Failed to upload receipt attachment. Please try again.',
      submitFailed: 'Failed to submit tithe. Please try again.'
    }
  }
};
